import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { ApiError, type BoardViewKind } from '@molt/shared';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { AvatarLabelsProvider } from '@/components/Avatar';
import { BoardFace } from '@/components/board/BoardFace';
import { OfflineBoardNotice } from '@/components/board/OfflineBoardNotice';
import {
  BoardError,
  BoardLoading,
  BoardSignInPrompt,
} from '@/components/board/BoardStates';
import { boardAvatarRoster } from '@/lib/avatar-labels';
import { saveCardOpenReturn, takeBoardCardReturn } from '@/lib/board-card-return';
import {
  accessibilityPreferencesQueryOptions,
  useSetReducedMotionOverride,
  useSetSingleKeyShortcutsDisabled,
} from '@/lib/accessibility-preferences';
import { boardFallbackFor, isCanonicalBoardPath } from '@/lib/board-address';
import {
  boardFilterCacheKey,
  boardFilterFromSearch,
  pruneBoardFilterFacets,
  type BoardFilterSelection,
  withBoardFilterSearchParams,
} from '@/lib/board-filter';
import { boardCustomFieldsQueryOptions } from '@/lib/custom-fields';
import {
  boardByShortLinkQueryOptions,
  boardContentsQueryOptions,
  canEdit,
  type BoardViewer,
} from '@/lib/board-face';
import { useBoardTemplate } from '@/lib/board-templates';
import { useRecordBoardRecentView } from '@/lib/board-stars';
import { ownBoardRole, useBoardMembers } from '@/lib/boards';
import { useBoardLive } from '@/lib/live-board';
import { isSuperadminSession, useSession } from '@/lib/session';
import { openKeyboardShortcuts } from '@/lib/keyboard-shortcuts';
import {
  loadOfflineBoardSnapshot,
  saveOfflineBoardSnapshot,
  type CachedOfflineBoard,
} from '@/lib/offline-sync';
import { parsePermalinkHash } from '@/lib/permalinks';
import {
  boardCollapsedListsQueryOptions,
  boardViewForViewport,
  boardViewPreferenceQueryOptions,
  collapsedListIdSet,
  useSetListCollapse,
  useSetBoardViewPreference,
} from '@/lib/view-state';

function useIsMobileOfflineViewport(): boolean {
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const query = window.matchMedia('(max-width: 767px)');
    setMobile(query.matches);
    const handleChange = () => setMobile(query.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return mobile;
}

function isNetworkApiError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === 'internal' &&
    typeof error.details === 'object' &&
    error.details !== null &&
    'network' in error.details
  );
}

/**
 * The board — `FE-13.1`, `FR-VIEW-001`.
 *
 * The first surface in this product that draws real data. It renders and it
 * does **not** mutate: every composer, drag and menu is a later chunk's, and
 * `C1-BRIEF.md` §6 says where each seam is.
 *
 * ---------------------------------------------------------------------------
 * ONE FILE, TWO URLS — `/b/<shortLink>/<slug>` AND `/b/<shortLink>`
 * ---------------------------------------------------------------------------
 * The slug is cosmetic: `GET /boards/by-short-link/:shortLink` looks up by the
 * short link alone and ignores it (`FR-EXP-015` AC4), so a link pasted without
 * one must still open the board.
 *
 * `{-$slug}` is this router's optional segment, and that it works **here** is
 * measured rather than assumed — `lib/board-face.test.ts`'s last describe block
 * loads the generated route tree and asserts all three of `/b/<x>/<slug>`,
 * `/b/<x>` and `/b/<x>/` match this route. (`@tanstack/react-router` 1.170.24
 * over `router-core` 1.171.20; the brief's fallback — two route files sharing
 * one component — is unnecessary at this version and the test is what will say
 * so if an upgrade changes the answer.)
 *
 * The `slug` param is therefore `string | undefined` and this file reads it
 * **never**. That is `FE-08.4`'s, below.
 *
 * ---------------------------------------------------------------------------
 * `FE-08.4` (chunk `D3`) — THE ADDRESS STORY, CLOSED HERE AND IN NO SECOND ROUTE
 * ---------------------------------------------------------------------------
 * This section used to say what `D3` *would* do. It now says what this file
 * *does*, because a stale claim in the file that owns the behaviour is worse
 * than no claim (`D2`'s lesson). Four behaviours, all `FE-08.4`'s, and all of
 * them in this file rather than in a second route matching the same URLs —
 * which is a coin toss the router resolves and nobody reads.
 *
 *  - **Slug canonicalisation** — `FR-EXP-015` AC1. The comparison is
 *    `isCanonicalBoardPath(location.pathname, board.path)`
 *    (`lib/board-address.ts`), against the **server-assembled** `path` on
 *    `boardAddressSchema` — never a client-rebuilt `/b/${shortLink}/${slug}`,
 *    which is a second spelling of the same string and will disagree on the
 *    first title the two slug rules treat differently. On a mismatch the route
 *    navigates with **`replace: true`, never a push**: a corrected URL that
 *    adds a history entry turns Back into a redirect loop. The correction is
 *    always one hop, because the destination *is* `path`, so the next
 *    comparison is an equality.
 *  - **The permission-aware fallback** — `D3-BRIEF.md` §5.3, three branches.
 *    ⚠ **The three are chosen on the viewer's SESSION, never on the error
 *    code.** "No such board" and "a board you may not see" are deliberately
 *    the same 404 (`CLAUDE.md`, `FR-PERM-008`), and this route does not try to
 *    tell them apart. See the branch comments below.
 *  - **The delete destination** — `onDeleted`, wired to `BoardFace`. A
 *    successful delete leaves the member standing on an address that no longer
 *    resolves, so the route leaves it, `replace: true` for the same reason.
 *  - **The browser tab title** — `FR-BOARD-003` AC1, whose third surface is
 *    "the browser tab title". See `useBoardDocumentTitle` below.
 *
 *  - **The card deep link** `/c/<KEY-NNNN or shortLink>/<slug>` opening a card *over*
 *    this board is `FE-14.1`'s (chunk `E1`), not this route's. `D3` shipped
 *    `routes/c.$shortLink.{-$slug}.tsx`, which resolves that address to **this
 *    board** and renders no card; `E1` is where the card goes on top.
 *
 * ---------------------------------------------------------------------------
 * ⚠ WHERE THIS ROUTE DELIBERATELY DIFFERS FROM ITS `/c/` SIBLING
 * ---------------------------------------------------------------------------
 * `routes/c.$shortLink.{-$slug}.tsx` renders a bespoke neutral `EmptyState`
 * ("This card isn't available") for its signed-in 404. **This route renders
 * `BoardError`, unchanged**, and that is §5.3's own word — *"signed in → the
 * not-found state, **unchanged**"*. The corroborating fact is in
 * `BoardStates.tsx`: agent H's finished file has no board-shaped not-found
 * component and §6.8 never asked for one, so inventing one inline here would
 * be building a component this chunk assigned to nobody. The card route needed
 * its own because it has no board behind it to fall back to; this route is the
 * board.
 *
 * ⚠ **Reported, not patched** (`D3-BRIEF.md` §10's last line): the sentence a
 * signed-in non-member reads inside that `BoardError` is the **server's**
 * `ApiError.message`, which is literally *"No such board"* — an existence
 * oracle in prose, after `boards.ts` went to some trouble not to be one on the
 * wire (`FR-PERM-008`'s edge case: *"must not disclose whether the board
 * exists, only that the user lacks access"*). §5.3 says render the state
 * unchanged, so it is rendered unchanged; the copy is `apps/server`'s to fix
 * and `apps/server` is not this chunk's to touch.
 *
 * ---------------------------------------------------------------------------
 * WHY THE DATA IS IN `useQuery` AND NOT IN A ROUTER `loader`
 * ---------------------------------------------------------------------------
 * A loader is the framework-idiomatic place for it and is wrong for this
 * chunk's requirement: a loader's throw lands in the router's
 * `errorComponent` (`RouteError`, wired as `defaultErrorComponent` in
 * `main.tsx`) and its pending state in `RoutePending` — both shell-shaped, both
 * centred, and neither is the board-shaped failure `FR-EXP-034` asks for. The
 * four states this chunk owes are `BoardStates.tsx`'s, so the route renders
 * them itself.
 */
export const Route = createFileRoute('/b/$shortLink/{-$slug}')({
  /*
   * `FE-04.11`'s shell reads this during matching — `lib/shell-layout.ts`. The
   * board face is the one surface the centred reading column is wrong for: a
   * `max-w-4xl` column cannot keep the header fixed while the strip scrolls
   * (`FR-VIEW-010` AC1), and would cap the board at 896px on a 1440px screen,
   * spending a third of `FE-04.12`'s density budget on margins. Drop this line
   * and nothing errors — the board simply comes back narrow.
   *
   * `full-bleed` also hands this route the whole overflow problem: the page
   * itself can no longer scroll, and everything below `<main>` is the board's
   * to manage.
   */
  staticData: { layout: 'full-bleed' },
  component: BoardRoute,
});

/**
 * `FR-BOARD-003` AC1's third surface — *"the header, the boards list and the
 * **browser tab title** all show the new name"*.
 *
 * ⚠ **`document.title` is set nowhere else in `apps/web`** (agent H measured
 * it; `index.html` ships the static `molt` and nothing has touched it since),
 * and no `FE-` item owns page titles product-wide. This closes the **board**
 * half of that criterion and nothing more: a title system — per-route titles,
 * a `<head>` manager, the router's own `head` option — is a product-wide
 * decision that would be taken here by accident.
 *
 * ⚠ **Two effects, not one, and the second one is why.** A single effect keyed
 * on the title that captured `document.title` in its own body would capture
 * *the previous board title* on a rename, and restore that on unmount — the
 * member leaves the board and the tab still names it, one rename behind. So:
 * a mount-only effect captures whatever the document arrived with and restores
 * exactly that, and a separate effect keyed on the string sets it.
 *
 * The title comes from `contents.data.board.title` — the same object
 * `BoardFace` draws its `<h1>` from, so the header and the tab cannot
 * disagree, and `FE-08.1`'s optimistic rename (`D3-BRIEF.md` §5.6 writes both
 * cache entries) moves them together.
 */
function useBoardDocumentTitle(boardTitle: string | undefined): void {
  const restoreTo = useRef<string | null>(null);

  useEffect(() => {
    restoreTo.current = document.title;
    return () => {
      if (restoreTo.current !== null) document.title = restoreTo.current;
    };
  }, []);

  useEffect(() => {
    if (boardTitle === undefined) return;
    document.title = `${boardTitle} · molt`;
  }, [boardTitle]);
}

function cardOpenHref(path: string, search: string): string {
  if (search === '') return path;
  const hashIndex = path.indexOf('#');
  if (hashIndex === -1) return `${path}?${search}`;
  return `${path.slice(0, hashIndex)}?${search}${path.slice(hashIndex)}`;
}

function BoardRoute(): ReactElement {
  const { shortLink } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const session = useSession();
  const rawSearch = router.history.location.search;
  const rawHash =
    location.hash === '' || location.hash.startsWith('#')
      ? location.hash
      : `#${location.hash}`;
  const { mutate: recordRecentView } = useRecordBoardRecentView();
  const lastRecordedRecentViewRef = useRef<string | null>(null);
  const [activeView, setActiveView] = useState<BoardViewKind>('board');
  const viewBoardRef = useRef<string | null>(null);
  const localViewSelectionRef = useRef(false);
  const mobileOfflineViewport = useIsMobileOfflineViewport();
  const offlineSnapshotUserId = session.profile?.id;
  const [cachedOfflineBoard, setCachedOfflineBoard] = useState<CachedOfflineBoard | null>(
    () => loadOfflineBoardSnapshot(offlineSnapshotUserId, shortLink),
  );

  /*
   * Two requests, not one — `lib/board-face.ts` carries the argument. The short
   * form: the contents entry has to be keyed `['boards', boardId, 'contents']`
   * because that is the only key a live frame can reach, and a combined query
   * would have to be keyed by the short link, which no frame carries.
   */
  const board = useQuery(boardByShortLinkQueryOptions(shortLink));
  const [filter, setFilter] = useState(() => boardFilterFromSearch(rawSearch));
  const [cardReturn, setCardReturn] = useState(() =>
    board.data === undefined ? undefined : takeBoardCardReturn(board.data.path),
  );
  const boardId = board.data?.id;
  const contents = useQuery({
    /*
     * `?? ''` is unreachable as a request: `enabled` is false until there is an
     * id, so the placeholder only ever names a cache entry nothing fetches. The
     * alternative — a conditional `useQuery` — is not one; hooks do not branch.
     */
    ...boardContentsQueryOptions(boardId ?? '', filter),
    enabled: boardId !== undefined,
  });
  const template = useBoardTemplate(boardId ?? '', boardId !== undefined);

  useEffect(() => {
    if (session.profile === null) return;
    if (contents.data === undefined) return;
    const resolvedBoardId = contents.data.board.id;
    if (resolvedBoardId === lastRecordedRecentViewRef.current) return;
    lastRecordedRecentViewRef.current = resolvedBoardId;
    recordRecentView({ boardId: resolvedBoardId });
  }, [contents.data, recordRecentView, session.profile]);

  useEffect(() => {
    setCachedOfflineBoard(loadOfflineBoardSnapshot(offlineSnapshotUserId, shortLink));
  }, [offlineSnapshotUserId, shortLink]);

  useEffect(() => {
    if (offlineSnapshotUserId === undefined) return;
    if (board.data === undefined || contents.data === undefined) return;
    saveOfflineBoardSnapshot({
      userId: offlineSnapshotUserId,
      shortLink,
      path: board.data.path,
      contents: contents.data,
    });
    setCachedOfflineBoard(loadOfflineBoardSnapshot(offlineSnapshotUserId, shortLink));
  }, [board.data, contents.data, offlineSnapshotUserId, shortLink]);

  /*
   * ---------------------------------------------------------------------------
   * WHO IS LOOKING — `FE-09.2`, chunk `D4`
   * ---------------------------------------------------------------------------
   * `canEdit` stopped meaning *"the board is not closed"* here. The viewer is
   * assembled once, in this one place, and `lib/board-face.ts` answers from it;
   * the predicate's own branch table and its three residuals are documented
   * there. The suite for this block is the `who is looking — FE-09.2` describe
   * in `b.$shortLink.{-$slug}.test.tsx`, which asserts by **effect** — whether
   * a control a person can press is on the board — and not by which prop was
   * passed.
   *
   * ⚠ **`enabled` is the session gate, and it is not optional.**
   * `GET /boards/:id/members` sits behind `requireUser` — a session is required
   * **even on a public board** — and `D1` bound the 401 redirect globally
   * against `isUnauthenticated`. Fired while signed out, this query would
   * redirect the anonymous visitor to `/sign-in`, bouncing them off the very
   * public board `FR-PERM-010` exists to let them read. There is no error
   * branch here and there must not be one: the gate is the `enabled`.
   * `D4-BRIEF.md` §3.4.
   *
   * ⚠ **`rolesKnown` is the members query *and* the session**, not the members
   * query alone — `ownBoardRole(members, undefined)` returns `null`, the same
   * value it returns for "you hold no row on this board". D3's spelling,
   * reused verbatim.
   *
   * ⚠ **The session conjunct is unobservable *from this route*, and it is kept
   * anyway** — measured, not assumed: removing it breaks no test in the suite,
   * because the two facts are already tied together here. `profile === null`
   * disables the members query, so `members.isSuccess` is false regardless, and
   * `canEdit`'s `!signedIn` branch returns before `rolesKnown` is ever read.
   * It stays because the derivation has to be right independently of this
   * caller's gate — the same argument `BoardFace` makes about its own copy —
   * and because `D4-BRIEF.md` §6.8 fixes this spelling. It is not redundant
   * code to tidy; it is `BoardMenuPanel`, whose trigger is gated on neither,
   * where the same conjunct actually bites.
   *
   * This is a **third** observer of `queryKeys.boards.members(boardId)`, beside
   * `BoardMenuPanel`'s (`open`) and `BoardFace`'s (`signedIn`). React Query
   * dedupes them against one key: one request, not three. Do not "fix" this
   * into a single observer — each site needs the answer at a different moment,
   * and an `enabled` is the house idiom for saying so. `D4-BRIEF.md` §5.5.
   */
  const signedIn = session.profile !== null;
  const viewPreference = useQuery({
    ...boardViewPreferenceQueryOptions(boardId ?? ''),
    enabled: signedIn && boardId !== undefined,
  });
  const collapsedLists = useQuery({
    ...boardCollapsedListsQueryOptions(boardId ?? ''),
    enabled: signedIn && boardId !== undefined,
  });
  const accessibilityPreferences = useQuery({
    ...accessibilityPreferencesQueryOptions(),
    enabled: signedIn && !session.isPending,
  });
  const setBoardViewPreference = useSetBoardViewPreference(boardId ?? '');
  const setListCollapse = useSetListCollapse();
  const setSingleKeyShortcutsDisabled = useSetSingleKeyShortcutsDisabled();
  const setReducedMotionOverride = useSetReducedMotionOverride();
  const liveStatus = useBoardLive({
    boardId,
    boardShortLink: board.data?.shortLink,
    enabled: signedIn && boardId !== undefined,
  });
  const members = useBoardMembers(boardId ?? '', signedIn && boardId !== undefined);
  const viewer: BoardViewer = {
    signedIn,
    role: ownBoardRole(members.data, session.profile?.id),
    rolesKnown: members.isSuccess && session.profile !== null,
    isSuper: isSuperadminSession(session),
  };

  useBoardDocumentTitle(contents.data?.board.title);

  useEffect(() => {
    if (boardId === viewBoardRef.current) return;
    viewBoardRef.current = boardId ?? null;
    localViewSelectionRef.current = false;
    setActiveView('board');
  }, [boardId]);

  useEffect(() => {
    if (viewPreference.data === undefined) return;
    if (localViewSelectionRef.current) return;
    setActiveView(
      boardViewForViewport(viewPreference.data.lastView, mobileOfflineViewport),
    );
  }, [mobileOfflineViewport, viewPreference.data]);

  useEffect(() => {
    setFilter(boardFilterFromSearch(rawSearch));
  }, [rawSearch]);

  useEffect(() => {
    if (board.data === undefined) return;
    setCardReturn(takeBoardCardReturn(board.data.path));
  }, [board.data?.path]);

  /*
   * `FR-EXP-015` AC1 — the corrected address, computed in render so the
   * effect's dependency is a **string**. A fresh object off `useQuery` every
   * render would re-fire the navigation on every render.
   *
   * ⚠ **`href`, not `to`.** `to` is matched against the generated route-path
   * union and carries its query in a separate `search` object, so a computed
   * path with an unparsed `?…` on the end is either rejected or silently
   * truncated; `navigate({ href })` parses the string into `to` + `search` +
   * `hash` first. `sign-in.tsx` carries the measurement, and the `/c/` sibling
   * takes the same route for the same reason.
   *
   * ⚠ **The search string is preserved here, and here is the only place it
   * can be.** `lib/board-address.ts` compares bare pathnames on purpose and
   * says so — so the raw history search (leading `?` included, or empty) is
   * concatenated on. `/b/<x>/old-slug?filter=overdue` keeps its filter across
   * the correction.
   *
   * ⚠ **`isError` is part of the guard, not just `data === undefined`** —
   * `useQuery` keeps the last successful `data` beside a *new* error. On the
   * `/c/` route that clause is a can't-happen invariant, because a success
   * unmounts the route on the next tick. **On this route it is reachable**:
   * this component stays mounted for as long as the board is open, so a
   * socket-driven refetch that fails leaves `board.data` populated while the
   * fallback below is rendering, and without this clause the effect would
   * navigate away from the state it was just told to show.
   *
   * ---------------------------------------------------------------------------
   * ⚠⚠ THE COMPARISON IS AGAINST **THIS MATCH'S** PATHNAME, NEVER `useLocation`
   * ---------------------------------------------------------------------------
   * `useLocation().pathname` is the **router's** current location, and this
   * component is still mounted for the frame in which that location has already
   * become the *destination of a navigation away from this board*. The
   * canonicalisation effect then sees `/` (or `/profile`, or any other address)
   * beside this board's `path`, calls them different, and **navigates back** —
   * so the board becomes a surface a member cannot leave. Every exit is
   * affected: the header's links, the workspace switcher, and `onDeleted`
   * below, which is how it was found: the delete succeeded, the announcement
   * fired, the DELETE was on the wire, and the router sat on the address of a
   * board that no longer existed. Nothing was red, and neither the effect nor
   * the delete is wrong when read alone.
   *
   * `Route.useMatch`'s `pathname` is **this match's own** address. It is the
   * stale `/b/<x>/old-slug` on arrival, becomes `path` when the correction
   * commits, and does not follow the router off this route — which is exactly
   * the question being asked here: *is the address that resolved to this board
   * the address the server calls canonical?*
   *
   * The raw history search is still the right source for the query string: it
   * preserves repeated filter keys exactly as the server contract spells them.
   */
  const matchPathname = Route.useMatch({ select: (match) => match.pathname });
  const canonicalDestination =
    board.data === undefined ||
    board.isError ||
    isCanonicalBoardPath(matchPathname, board.data.path)
      ? undefined
      : `${board.data.path}${rawSearch}${rawHash}`;

  useEffect(() => {
    if (canonicalDestination === undefined) return;
    /*
     * ⚠ `replace`, never a push. A push leaves the stale address one Back
     * press away, where it resolves and forwards again — the redirect loop
     * `D3-BRIEF.md` §5.4 forbids. A push and a replace leave the same
     * `location.pathname` behind them, so the test walks Back rather than
     * reading the final URL.
     */
    void navigate({ href: canonicalDestination, replace: true });
  }, [canonicalDestination, navigate]);

  const handleFilterChange = useCallback(
    (nextFilter: BoardFilterSelection): void => {
      setFilter(nextFilter);

      const params = withBoardFilterSearchParams(rawSearch, nextFilter);
      const search = params.toString();
      router.history.replace(`${location.pathname}${search === '' ? '' : `?${search}`}`);
      router.history.flush();
    },
    [location.pathname, rawSearch, router.history],
  );

  const handleViewChange = useCallback(
    (view: BoardViewKind): void => {
      localViewSelectionRef.current = true;
      setActiveView(view);
      if (!signedIn || boardId === undefined || contents.data?.board.closed === true)
        return;
      setBoardViewPreference.mutate(view);
    },
    [boardId, contents.data?.board.closed, setBoardViewPreference, signedIn],
  );

  /**
   * `FR-SEARCH-031` — the definitions the custom-field facets are pruned
   * against. Not gated on `signedIn`: `GET /boards/:id/custom-fields` answers
   * **404, never 401**, outside the read scope, so it cannot trip the global
   * 401 redirect and eject an anonymous visitor — `FE-61.2`'s bug, and the
   * reason the gate is the reflex. Whether it answers 200 on a public board
   * to that visitor is unasserted and unclaimed: if it 404s, this query never
   * settles, the prune below imposes nothing, and the server arm still
   * filters correctly. Same query key as `BoardFace`'s own observer, so this
   * is one fetch.
   */
  const boardCustomFields = useQuery({
    ...boardCustomFieldsQueryOptions(boardId ?? ''),
    enabled: boardId !== undefined,
  });

  useEffect(() => {
    if (contents.data === undefined) return;
    const memberIds =
      signedIn && members.isSuccess
        ? members.data.map((member) => member.id)
        : signedIn
          ? undefined
          : [];
    const pruned = pruneBoardFilterFacets(filter, {
      memberIds,
      labelIds: contents.data.labels.map((label) => label.id),
      /**
       * ⚠⚠ `undefined` UNTIL THE DEFINITIONS QUERY HAS SETTLED, AND THIS IS
       * THE WHOLE GUARD — `FR-SEARCH-031` AC5's own trap, from the other
       * side.
       *
       * The definitions arrive on their own request, separate from the
       * board's contents, so `boardCustomFields.data ?? []` here would mean
       * "no field on this board offers a facet" for the one render before it
       * resolves — and this effect would prune every custom-field criterion a
       * shared URL carried, rewrite the URL without them, and lose them
       * permanently. `undefined` says "not known yet, impose no pruning",
       * exactly as `memberIds` above already does for the same reason.
       */
      customFields: boardCustomFields.isSuccess ? boardCustomFields.data : undefined,
    });

    if (boardFilterCacheKey(pruned) === boardFilterCacheKey(filter)) return;
    handleFilterChange(pruned);
  }, [
    boardCustomFields.data,
    boardCustomFields.isSuccess,
    contents.data,
    filter,
    handleFilterChange,
    members.data,
    members.isSuccess,
    signedIn,
  ]);

  const parsedHash = typeof window === 'undefined' ? null : parsePermalinkHash(rawHash);
  /*
   * ⚠⚠ `signedIn &&` IS THE WHOLE OF `FE-61.2`'s FIRST RESIDUAL, AND WITHOUT IT
   * A PERMALINK EJECTS AN ANONYMOUS VISITOR OFF A BOARD `FR-PERM-010` AC1
   * GRANTS THEM — with nothing red anywhere.
   *
   * `BoardFace` latches this into `setMenuOpen(true)` in an effect
   * (`BoardFace.tsx`, `useEffect` on `[initialBoardMenuSection]`), and
   * `BoardMenuPanel` gates fourteen queries on `open` **alone**. Five of them
   * — `/members`, `/member-permission`, `/self-join`, `/capacity` and
   * `/watch` — answer **401** to an anonymous caller even on a public board,
   * and `main.tsx` binds any 401 that is not the shell's own session probe to a
   * global `/sign-in` redirect. So this expression, computed with no session
   * conjunct, was the only thing standing between `#activity-<id>` and
   * `/sign-in?next=%2Fb%2F…%23activity-…`.
   *
   * Measured on the unfixed build rather than reasoned about, in
   * `e2e/FR-PERM-010.spec.ts`: all five fired, all five answered 401, and the
   * visitor ended on `/sign-in`. The sixth, `/activity`, fired zero times —
   * fan-out 9 had already closed that one with an `enabled` on
   * `BoardActivitySection`'s feed query. Both gates stand: they close the same
   * 401 from two different heights.
   *
   * ⚠ **The panel is the wrong place to fix this, and its docstring says so**:
   * *"The panel's gate is `open` alone and stays that way... `BoardFace`
   * renders the menu trigger only when `signedIn`. That gate is what makes this
   * one safe — a chunk that makes the trigger anonymous-reachable must widen
   * this `enabled` in the same commit."* The permalink made the trigger
   * anonymous-reachable; the repair is to take that back here, not to widen
   * fourteen `enabled`s there.
   *
   * ⚠ **This does not break the permalink for the people it works for.**
   * `signedIn` is `session.profile !== null`, which is `false` on the first
   * paint of EVERY load, so this goes `undefined -> 'activity'` and `BoardFace`'s
   * effect fires on that transition rather than on mount. That is reasoning,
   * and reasoning is not why it is believed: the signed-in half of
   * `FR-PERM-010.spec.ts` follows the identical permalink with a session and
   * asserts the panel opens at Activity.
   */
  const initialBoardMenuSection =
    signedIn && parsedHash?.target === 'activity' ? ('activity' as const) : undefined;
  const anonymousPublicNonTemplateFacet =
    !signedIn && template.isError && template.error.code === 'not_found';

  function renderBoardFace(
    faceContents: NonNullable<typeof contents.data>,
    options: { readonly offlineCachedAt?: string | undefined } = {},
  ): ReactElement {
    const renderTemplate =
      options.offlineCachedAt === undefined
        ? anonymousPublicNonTemplateFacet
          ? null
          : (template.data ?? null)
        : null;

    /*
     * Clashing initials are separated across exactly this board — its member
     * list (signed-in only) plus every card's members — and only below this
     * provider. The shell header, `AccountMenu` and the notification bell are
     * rendered by `__root.tsx` beside the `<Outlet />`, not inside it, so they
     * keep the server's initials.
     */
    return (
      <AvatarLabelsProvider roster={boardAvatarRoster(members.data, faceContents)}>
        <BoardFace
          contents={faceContents}
          canEdit={canEdit(faceContents.board, viewer)}
          template={renderTemplate}
          templateLoading={
            options.offlineCachedAt === undefined
              ? anonymousPublicNonTemplateFacet
                ? false
                : template.isLoading
              : false
          }
          liveStatus={liveStatus}
          offlineCachedAt={options.offlineCachedAt}
          cardReturn={cardReturn}
          onCardOpen={(intent) => {
            saveCardOpenReturn({ ...intent, returnBoardPath: board.data?.path });
            const params = new URLSearchParams(rawSearch);
            if (intent.openPanel === 'dates') params.set('panel', 'dates');
            const search = params.toString();
            const href = cardOpenHref(intent.path, search);
            void navigate({ href });
          }}
          /*
           * ⚠ **The route is the required caller** (`BoardFaceProps.onDeleted`'s
           * own docstring says so): the board this face was drawn from no longer
           * exists once this fires, so an unwired `onDeleted` leaves the member
           * standing on a dead address with a rendered board still on screen.
           *
           * `to: '/'`, not `href`: the destination is a literal route path with no
           * search to carry — and carrying this board's search string to the
           * boards landing page would be meaningless there. `replace: true`
           * because the history entry behind us points at a board that is gone,
           * and Back must not walk into a 404 the member cannot fix.
           */
          onDeleted={() => void navigate({ to: '/', replace: true })}
          onLeft={() => void navigate({ to: '/', replace: true })}
          filter={filter}
          onFilterChange={handleFilterChange}
          activeView={faceContents.board.closed ? 'board' : activeView}
          viewPreferenceBusy={setBoardViewPreference.isPending}
          onViewChange={signedIn ? handleViewChange : undefined}
          initialBoardMenuSection={initialBoardMenuSection}
          collapsedListIds={collapsedListIdSet(collapsedLists.data)}
          accessibilityPreferences={accessibilityPreferences.data ?? null}
          accessibilityPreferencesBusy={
            accessibilityPreferences.isLoading ||
            setSingleKeyShortcutsDisabled.isPending ||
            setReducedMotionOverride.isPending
          }
          onOpenShortcuts={openKeyboardShortcuts}
          onSingleKeyShortcutsDisabledChange={(disabled) =>
            setSingleKeyShortcutsDisabled.mutate(disabled)
          }
          onReducedMotionOverrideChange={(enabled) =>
            setReducedMotionOverride.mutate(enabled)
          }
          onListCollapseChange={
            signedIn
              ? (listId, collapsed) => {
                  setListCollapse.mutate({
                    boardId: faceContents.board.id,
                    listId,
                    collapsed,
                  });
                }
              : undefined
          }
        />
      </AvatarLabelsProvider>
    );
  }

  /*
   * The error branches are ordered resolver-then-contents and each retries
   * **the query that failed**. A "Try again" that refetches the contents after
   * the resolver 404'd passes every unit test and does nothing in a browser.
   *
   * The error object is passed through untouched: no branching on `not_found`
   * versus `forbidden` (see the existence-oracle note above), and no
   * `isUnauthenticated` handling — that redirect is `FE-06.1`'s and is bound
   * once, globally, not per route.
   */
  if (board.isError) {
    if (mobileOfflineViewport && isNetworkApiError(board.error)) {
      if (cachedOfflineBoard !== null) {
        return renderBoardFace(cachedOfflineBoard.contents, {
          offlineCachedAt: cachedOfflineBoard.cachedAt,
        });
      }
      return (
        <main className="flex min-h-[calc(100vh-4rem)] items-start justify-center bg-board p-4">
          <div className="w-full max-w-xl">
            <OfflineBoardNotice
              mode="not-available"
              boardAddress={`${location.pathname}${rawSearch}${rawHash}`}
              onRetry={() => void board.refetch()}
            />
          </div>
        </main>
      );
    }

    /*
     * ⚠ **The pending hold, and it is the ordinary path on this route rather
     * than an edge case** (`D3-BRIEF.md` §5.3, first row). `GET /me` and the
     * resolver go out together on a pasted link, and the 404 routinely lands
     * first — at which instant `session.error` is still `null`, so
     * `isSignedOut` is `false` and the signed-in branch would tell a genuinely
     * signed-out visitor *"No such board"* for a frame. That is the exact
     * sentence `FR-PERM-001` AC1 forbids, and it would have read as a flicker
     * rather than as a bug.
     *
     * Inside the error branch, not above it: a top-level hold would stall the
     * board face for every signed-in member on every cold load.
     */
    if (session.isPending) return <BoardLoading />;

    /*
     * ⚠ `session.isSignedOut` is `isUnauthenticated(error)`, **never
     * `!profile`** — an expired session keeps its last-good profile beside the
     * error, so a `!profile` test renders the signed-in branch to somebody
     * whose session has just died.
     */
    switch (boardFallbackFor({ error: board.error, isSignedOut: session.isSignedOut })) {
      case 'sign-in':
        /*
         * `FR-PERM-001` AC1 clause 1, and the one branch this chunk added.
         * `BoardSignInPrompt` owns the copy and the `?next=` round trip
         * (agent H); the return path is the address the visitor actually
         * pasted, **search string included**, because signing in may be
         * exactly what makes it resolve.
         */
        return (
          <BoardSignInPrompt returnPath={`${location.pathname}${rawSearch}${rawHash}`} />
        );
      case 'not-found':
      case 'error':
        /*
         * Two branch values, one component, on purpose — see "where this route
         * deliberately differs from its `/c/` sibling" above. `BoardError`
         * retries **the resolver**, which is the query that failed.
         */
        return <BoardError error={board.error} onRetry={() => void board.refetch()} />;
    }
  }
  /*
   * ⚠ **The contents failure takes no session-aware fallback**, and that is a
   * decision rather than an omission. The contents query is `enabled` only
   * once the resolver has succeeded, so reaching this line means the visitor
   * could read this board a moment ago — a sign-in prompt would be answering a
   * question nobody asked, and the honest answer is the retry.
   */
  if (contents.isError) {
    if (mobileOfflineViewport && isNetworkApiError(contents.error)) {
      if (cachedOfflineBoard !== null) {
        return renderBoardFace(cachedOfflineBoard.contents, {
          offlineCachedAt: cachedOfflineBoard.cachedAt,
        });
      }
      return (
        <main className="flex min-h-[calc(100vh-4rem)] items-start justify-center bg-board p-4">
          <div className="w-full max-w-xl">
            <OfflineBoardNotice
              mode="not-available"
              boardAddress={`${location.pathname}${rawSearch}${rawHash}`}
              onRetry={() => void contents.refetch()}
            />
          </div>
        </main>
      );
    }
    return <BoardError error={contents.error} onRetry={() => void contents.refetch()} />;
  }

  if (template.isError && !anonymousPublicNonTemplateFacet) {
    return <BoardError error={template.error} onRetry={() => void template.refetch()} />;
  }

  /*
   * One loading branch for both requests. Testing `contents.data` rather than
   * either query's `isPending` is what makes the gap between them invisible:
   * while the resolver is in flight the contents query is disabled, and a
   * disabled query is `isPending` forever — so an `isPending` test would render
   * the skeleton correctly by accident and stop the day either query gains
   * `placeholderData`.
   */
  if (contents.data === undefined) return <BoardLoading />;

  /*
   * `canEdit` is computed here, once, and handed down. Nothing below this line
   * reads `board.closed` — see `lib/board-face.ts` for what that function can
   * and cannot know today, and what `D1`/`D4` change when they land.
   */
  return renderBoardFace(contents.data);
}
